/**
 * The drill-down behind one heatmap cell: the actual subject rows the
 * aggregate counted, each with a Watch button that plays the moment on the
 * game's video — in a layered modal, without leaving the builder.
 *
 * Two layers. The list is fetched once when the modal opens (the parent
 * mounts a fresh modal per click, so the spec/bin pair is fixed for its
 * lifetime). The video layer sits above the list; Escape and backdrop
 * clicks dismiss the top layer first, so closing the video returns to the
 * list rather than to the pitch.
 *
 * The seek uses the same frameToVideoTime the match page uses, fed the
 * game's own calibration from the examples response. One honest limit:
 * without the match page's data-side halftime detection this uses the
 * mapping's default frame convention, so a second-half seek on a game
 * whose frames include the halftime break can land off — the clip starts
 * CLIP_LEAD_S early and the player has controls, which covers review use.
 */
import { useEffect, useState } from 'react'

import { NO_FRAME_RATE_NOTE, servedFps } from '../../lib/frameRate'
import {
  formatGameTime, frameToVideoTime, syncFieldsOf, type SyncState,
} from '../../lib/viz/frameTime'
import { show } from './format'
import type { ExampleVideo, ExamplesResult } from './run'
import { examples } from './run'
import type { AnalysisSpec } from './spec'

/** Start the clip a little before the moment, for context. */
const CLIP_LEAD_S = 5

/** A playable example: its video and the recorded rate frames map at. */
type Playing = { video: ExampleVideo; frame: number; fps: number }

const syncFor = (v: ExampleVideo, fps: number): SyncState => ({
  videoGameStartTime: v.video_game_start_time ?? 0,
  videoHalftimeStartTime: v.video_halftime_start_time,
  videoHalftimeEndTime: v.video_halftime_end_time,
  ...syncFieldsOf(v),
  fps,
})

export default function ExamplesModal({ spec, bin, title, onClose }: {
  spec: AnalysisSpec
  bin: Record<string, unknown>
  title: string
  onClose: () => void
}) {
  const [data, setData] = useState<ExamplesResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [playing, setPlaying] = useState<Playing | null>(null)

  useEffect(() => {
    let live = true
    examples(spec, bin)
      .then((r) => { if (live) setData(r) })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : String(e)) })
    return () => { live = false }
    // The parent mounts a fresh modal per click, so spec/bin never change
    // under a mounted instance; fetching once on mount is the whole contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Escape dismisses the top layer: the video first, then the list.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      if (playing) setPlaying(null)
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [playing, onClose])

  const gameIdx = data ? data.columns.indexOf('game') : -1
  const frameIdx = data ? data.columns.indexOf(data.frame_column) : -1

  const startAt = playing
    ? Math.max(0, frameToVideoTime(syncFor(playing.video, playing.fps), playing.frame) - CLIP_LEAD_S)
    : 0

  return (
    <div className="vb-drill" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="vb-drill__panel" onClick={(e) => e.stopPropagation()}>
        <div className="vb-drill__head">
          <strong>{title}</strong>
          <button
            type="button" className="vb-drill__close" onClick={onClose}
            aria-label="Close examples"
          >
            {'\u00D7'}
          </button>
        </div>

        {error ? <p className="adm-warn">{error}</p> : null}
        {!data && !error ? <p className="adm-note">Loading examples…</p> : null}

        {data ? (
          <>
            <p className="adm-note">
              {data.row_count} example{data.row_count === 1 ? '' : 's'} — each
              row is one {spec.subject.type}; Watch plays the moment on the
              game video.
            </p>
            <div className="vb-drill__scroll">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th aria-label="Watch" />
                    {data.columns.map((c) => <th key={c}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => {
                    const game = String(row[gameIdx] ?? '')
                    const frame = Number(row[frameIdx])
                    const video = data.video[game]
                    // the video's recorded rate maps the seek; with none
                    // there is no Watch, rather than a seek at a guess
                    const fps = servedFps(video?.fps)
                    return (
                      <tr key={i}>
                        <td>
                          {video && fps !== null && Number.isFinite(frame) ? (
                            <button
                              type="button" className="vb-drill__watch"
                              onClick={() => setPlaying({ video, frame, fps })}
                            >
                              {'\u25B6'} {formatGameTime(frame, fps)}
                            </button>
                          ) : video && fps === null ? (
                            <span className="vb-drill__novideo"
                                  title={NO_FRAME_RATE_NOTE}>no frame rate</span>
                          ) : (
                            <span className="vb-drill__novideo">no video</span>
                          )}
                        </td>
                        {row.map((v, j) => <td key={j}>{show(v)}</td>)}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>

      {playing ? (
        <div
          className="vb-drill__player"
          onClick={(e) => { e.stopPropagation(); setPlaying(null) }}
        >
          <div className="vb-drill__player-box" onClick={(e) => e.stopPropagation()}>
            <div className="vb-drill__head">
              <strong>
                {playing.video.game_code}
                {' \u00b7 '}
                {formatGameTime(playing.frame, playing.fps)}
              </strong>
              <button
                type="button" className="vb-drill__close"
                onClick={() => setPlaying(null)} aria-label="Close video"
              >
                {'\u00D7'}
              </button>
            </div>
            <video
              key={`${playing.video.game_code}-${playing.frame}`}
              className="vb-drill__video"
              src={playing.video.video_url}
              controls autoPlay
              onLoadedMetadata={(e) => { e.currentTarget.currentTime = startAt }}
            />
          </div>
        </div>
      ) : null}
    </div>
  )
}
